import { useEffect } from "react";
import { z } from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarIcon, Clock, DollarSign, User, Tag, Check, X, Loader2, MapPin } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { sendNotification } from "@/lib/emailService";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";

interface Service {
  id: string;
  photographer_id: string;
  name: LocalizedContent;
  category: string;
  description: LocalizedContent;
  duration_minutes: number;
  base_price: number;
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  service?: Service;
}

export function BookingModal({ isOpen, onClose, service }: BookingModalProps) {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const { formatPrice } = useCurrency();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preferredTime, setPreferredTime] = useState<string | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const { toast } = useToast();

  // Promo/Referral Code State
  const [promoCode, setPromoCode] = useState("");
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<{
    code: string;
    amount: number;
    type: string;
    id: string;
    code_type: 'PROMO' | 'REFERRAL';
    salesperson_name?: string;
    commission_amount?: number;
  } | null>(null);
  const [promoMessage, setPromoMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Reset promo code state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPromoCode("");
      setAppliedDiscount(null);
      setPromoMessage(null);
    }
  }, [isOpen]);

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim() || !service) return;

    setIsValidatingCode(true);
    setPromoMessage(null);

    try {
      const { data, error } = await (supabase as any).rpc('validate_code', {
        p_code: promoCode.trim(),
        p_order_amount: service.base_price,
        p_customer_email: '' // Will be filled in on submit
      });

      if (error) throw error;

      const result = data?.[0] as any;

      if (result?.is_valid) {
        setAppliedDiscount({
          code: promoCode.trim(),
          amount: result.discount_amount,
          type: result.code_type,
          id: result.code_id,
          code_type: result.code_type,
          salesperson_name: result.salesperson_name,
          commission_amount: result.commission_amount
        });

        const codeTypeLabel = result.code_type === 'REFERRAL' ? 'Referral Code' : 'Promo Code';
        setPromoMessage({ text: result.message, type: 'success' });
        toast({
          title: `${codeTypeLabel} Applied`,
          description: result.code_type === 'REFERRAL'
            ? `Referred by ${result.salesperson_name}! You saved ${formatPrice(result.discount_amount)}!`
            : `You saved ${formatPrice(result.discount_amount)}!`,
        });
      } else {
        setAppliedDiscount(null);
        setPromoMessage({ text: result?.message || 'Invalid code', type: 'error' });
      }
    } catch (err) {
      console.error('Code validation error:', err);
      setPromoMessage({ text: 'Failed to validate code', type: 'error' });
    } finally {
      setIsValidatingCode(false);
    }
  };

  const finalPrice = service ? (service.base_price - (appliedDiscount?.amount || 0)) : 0;


  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}${t('common.minutesShort', 'min')}`;
    if (mins === 0) return `${hours}${t('common.hoursShort', 'h')}`;
    return `${hours}${t('common.hoursShort', 'h')} ${mins}${t('common.minutesShort', 'min')}`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!service) throw new Error(t('booking.errorNoService', 'No service selected'));

      const form = new FormData(e.currentTarget);
      const firstName = String(form.get('firstName') || '').trim();
      const lastName = String(form.get('lastName') || '').trim();
      const email = String(form.get('email') || '').trim();
      const phone = String(form.get('phone') || '').trim();
      const location = String(form.get('location') || '').trim();
      const notes = String(form.get('notes') || '').trim();
      const preferredDate = selectedDate ? selectedDate.toISOString().split('T')[0] : '';

      // Form Validation Schema
      const bookingSchema = z.object({
        firstName: z.string().min(2, t('validation.firstNameMin', 'First name must be at least 2 characters')),
        lastName: z.string().min(2, t('validation.lastNameMin', 'Last name must be at least 2 characters')),
        email: z.string().email(t('validation.emailInvalid', 'Please enter a valid email address')),
        phone: z.string().min(8, t('validation.phoneRequired', 'WhatsApp number is required (min 8 characters)')),
        location: z.string().min(3, t('validation.locationRequired', 'Session location is required')),
        preferredDate: z.string().min(1, t('validation.dateRequired', 'Please select a date')).refine((date) => {
          const d = new Date(date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return d >= today;
        }, {
          message: t('validation.datePast', 'Date cannot be in the past')
        }),
        preferredTime: z.string().min(1, t('validation.timeRequired', 'Please select a preferred time'))
      });

      const formData = {
        firstName,
        lastName,
        email,
        phone,
        location,
        preferredDate,
        preferredTime: preferredTime || ''
      };

      const validationResult = bookingSchema.safeParse(formData);

      if (!validationResult.success) {
        // Show the first error message
        const firstError = validationResult.error.errors[0];
        throw new Error(firstError.message);
      }

      // Map the preferred time window to concrete start/end times
      const timeMap: Record<string, { start: string; end: string }> = {
        'morning': { start: '09:00:00', end: '12:00:00' },
        'afternoon': { start: '12:00:00', end: '17:00:00' },
        'evening': { start: '17:00:00', end: '20:00:00' },
        'golden-hour': { start: '18:00:00', end: '19:00:00' },
        'default': { start: '12:00:00', end: '13:00:00' },
      };

      const { start, end } = timeMap[preferredTime || 'default'];

      // AUTO-APPLY PROMO CODE Logic
      // If user entered a code but didn't click apply, we try to apply it now.
      let effectiveDiscount = appliedDiscount;

      if (!effectiveDiscount && promoCode.trim()) {
        setIsValidatingCode(true);
        try {
          const { data, error } = await (supabase as any).rpc('validate_code', {
            p_code: promoCode.trim(),
            p_order_amount: service.base_price,
            p_customer_email: email
          });

          if (error) throw error;
          const result = data?.[0] as any;

          if (result?.is_valid) {
            effectiveDiscount = {
              code: promoCode.trim(),
              amount: result.discount_amount,
              type: result.code_type,
              id: result.code_id,
              code_type: result.code_type,
              salesperson_name: result.salesperson_name,
              commission_amount: result.commission_amount
            };
            // Update UI state too
            setAppliedDiscount(effectiveDiscount);
            setPromoMessage({ text: result.message, type: 'success' });

            const codeTypeLabel = result.code_type === 'REFERRAL' ? 'Referral Code' : 'Promo Code';
            toast({
              title: t('booking.codeAutoAppliedTitle', `${codeTypeLabel} Auto-Applied`),
              description: result.code_type === 'REFERRAL'
                ? `Referred by ${result.salesperson_name}! You saved ${formatPrice(result.discount_amount)}.`
                : `We applied '${promoCode.trim()}' for you! You saved ${formatPrice(result.discount_amount)}.`,
            });
          } else {
            // If code is invalid, block submission and warn user
            setPromoMessage({ text: result?.message || 'Invalid code', type: 'error' });
            throw new Error(t('booking.codeInvalid', {
              defaultValue: `Invalid code: ${result?.message}. Please clear the code or enter a valid one.`,
              message: result?.message
            }));
          }
        } finally {
          setIsValidatingCode(false);
        }
      }

      // Calculate final price based on EFFECTIVE discount (state might be stale if we just applied it)
      const priceToCharge = service.base_price - (effectiveDiscount?.amount || 0);

      // Validate promo code per-user limit BEFORE creating booking
      if (effectiveDiscount) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: existingRedemptions, error: checkError } = await (supabase as any)
          .from('promo_code_redemptions')
          .select('id')
          .eq('promo_code_id', effectiveDiscount.id)
          .eq('customer_email', email)
          .neq('status', 'CANCELLED');

        if (checkError) throw checkError;

        const { data: promoCodeData } = await supabase
          .from('promo_codes')
          .select('per_user_limit')
          .eq('id', effectiveDiscount.id)
          .single();

        const perUserLimit = promoCodeData?.per_user_limit || 1;

        if (existingRedemptions && existingRedemptions.length >= perUserLimit) {
          const limitMsg = t('booking.promoLimitReachedDesc', {
            defaultValue: `You have already used this promo code ${perUserLimit} time(s) with this email address.`,
            limit: perUserLimit
          });

          setPromoMessage({ text: limitMsg, type: 'error' });

          toast({
            title: t('booking.promoLimitReachedTitle', "Promo Code Limit Reached"),
            description: limitMsg,
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Use secure RPC to create time slot + booking in one transaction (bypasses RLS safely)
      const { data: rpcData, error: rpcError } = await (supabase as any).rpc('create_booking_request', {
        p_service_id: service.id,
        p_photographer_id: service.photographer_id,
        p_slot_date: preferredDate,
        p_start_time: start,
        p_end_time: end,

        p_total_amount: priceToCharge, // Use calculated price to charge
        p_customer_name: `${firstName} ${lastName}`,
        p_customer_email: email,
        p_customer_phone: phone || null,
        p_notes: notes || null,
        p_code_id: effectiveDiscount?.id || null,
        p_code_type: effectiveDiscount?.code_type || null,
        p_discount_amount: effectiveDiscount?.amount || 0,
        p_commission_amount: effectiveDiscount?.commission_amount || 0
      });

      if (rpcError) throw rpcError;

      toast({
        title: t('booking.successTitle', 'Booking request sent!'),
        description: t('booking.successMessage', "We'll contact you within 24 hours to confirm your session."),
      });

      // Note: Redemption records and usage counters are now handled atomically in the backend
      // via the create_booking_request RPC.

      // Send email notification with Premium Card Layout
      await sendNotification(
        `New Booking Request: ${getLocalized(service.name)}`,
        `Booking Request`,
        `
        <!-- Highlight Box -->
        <div class="info-box">
            <div class="info-label">Selected Service</div>
            <div class="info-value text-center" style="font-size: 18px;">${getLocalized(service.name)}</div>
            <div class="text-center highlight" style="margin-top:5px;">
              ${effectiveDiscount ?
          `<span style="text-decoration: line-through; opacity: 0.7; font-size: 0.8em;">${formatPrice(service.base_price)}</span> ` +
          `<span style="color: #22c55e;">${formatPrice(priceToCharge)}</span>`
          : formatPrice(service.base_price)}
            </div>
            ${effectiveDiscount ? `<div style="text-align:center; font-size:12px; color:${effectiveDiscount.code_type === 'REFERRAL' ? '#3b82f6' : '#22c55e'}; margin-top:2px;">${effectiveDiscount.code_type === 'REFERRAL' ? 'Referral' : 'Promo'}: ${effectiveDiscount.code} (-${formatPrice(effectiveDiscount.amount)})${effectiveDiscount.salesperson_name ? ` by ${effectiveDiscount.salesperson_name}` : ''}</div>` : ''}
        </div>

        <div class="section-title">Client Information</div>
        <table width="100%">
            <tr>
                <td width="50%" class="info-row">
                    <span class="info-label">Full Name</span>
                    <div class="info-value">${firstName} ${lastName}</div>
                </td>
                <td width="50%" class="info-row">
                    <span class="info-label">Email Address</span>
                    <div class="info-value"><a href="mailto:${email}" style="color:#111;text-decoration:none;">${email}</a></div>
                </td>
            </tr>
            <tr>
                <td class="info-row" style="padding-top:15px;">
                     <span class="info-label">Phone Number</span>
                     <div class="info-value">${phone || 'Not provided'}</div>
                </td>
                 <td class="info-row" style="padding-top:15px;">
                     <span class="info-label">Location</span>
                     <div class="info-value">${form.get('location') || 'Not specified'}</div>
                </td>
            </tr>
        </table>

        ${effectiveDiscount ? `
        <div class="section-title">Payment Details</div>
        <table width="100%">
            <tr>
                <td width="50%" class="info-row">
                    <span class="info-label">Original Price</span>
                    <div class="info-value">${formatPrice(service.base_price)}</div>
                </td>
                <td width="50%" class="info-row">
                    <span class="info-label">Promo Code</span>
                    <div class="info-value" style="color:#22c55e; font-weight:bold;">${effectiveDiscount.code}</div>
                </td>
            </tr>
            <tr>
                <td class="info-row" style="padding-top:15px;">
                    <span class="info-label">Discount</span>
                    <div class="info-value" style="color:#22c55e;">-${formatPrice(effectiveDiscount.amount)}</div>
                </td>
                <td class="info-row" style="padding-top:15px;">
                    <span class="info-label">Final Price</span>
                    <div class="info-value" style="font-weight:bold; font-size:18px; color:#22c55e;">${formatPrice(priceToCharge)}</div>
                </td>
            </tr>
        </table>
        ` : ''}

        <div class="section-title">Session Details</div>
        <table width="100%">
            <tr>
                <td width="50%" class="info-row">
                    <span class="info-label">Preferred Date</span>
                    <div class="info-value">${preferredDate}</div>
                </td>
                <td width="50%" class="info-row">
                    <span class="info-label">Time Slot</span>
                    <div class="info-value" style="text-transform:capitalize;">${preferredTime || 'Any'}</div>
                </td>
            </tr>
        </table>

        <div class="section-title">Additional Notes</div>
        <div style="background:#fff; border:1px solid #eee; padding:15px; border-radius:6px; color:#555; font-style:italic; font-size:14px;">
            "${notes || 'No specific notes provided.'}"
        </div>
        `,
        email,
        `${window.location.origin}/admin`,
        "View in Dashboard"
      );

      // Reset form and promo state
      setPromoCode("");
      setAppliedDiscount(null);
      setPromoMessage(null);
      onClose();
    } catch (err: any) {
      console.error('Booking error:', err);
      toast({
        title: t('booking.errorTitle', 'Booking failed'),
        description: err?.message || t('common.errorGeneric', 'Please try again later.'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!service) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('booking.modalTitle', 'Book Your Session')}</DialogTitle>
        </DialogHeader>

        {/* Service Summary */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-lg">{getLocalized(service.name)}</h3>
          <p className="text-muted-foreground text-sm">{getLocalized(service.description)}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDuration(service.duration_minutes)}
              </div>
              <div className="flex items-center gap-1 font-semibold text-primary">
                {/* Price moved to bottom */}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Information */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('booking.contactInfo', 'Contact Information')}
            </h4>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('booking.firstName', 'First Name *')}</Label>
                <Input id="firstName" name="firstName" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('booking.lastName', 'Last Name *')}</Label>
                <Input id="lastName" name="lastName" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('booking.email', 'Email *')}</Label>
              <Input id="email" name="email" type="email" required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('booking.phone', 'WhatsApp Number *')}</Label>
              <Input id="phone" name="phone" type="tel" placeholder="+1 234 567 8900" required />
            </div>
          </div>

          {/* Session Details */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {t('booking.sessionDetails', 'Session Details')}
            </h4>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('booking.date', 'Preferred Date *')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!selectedDate && 'text-muted-foreground'}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? selectedDate.toLocaleDateString() : t('booking.selectDate', 'Pick a date')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferredTime">{t('booking.time', 'Preferred Time *')}</Label>
                <Select value={preferredTime} onValueChange={setPreferredTime} required>
                  <SelectTrigger>
                    <SelectValue placeholder={t('booking.selectTime', 'Select time')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">{t('booking.timeMorning', 'Morning (9AM - 12PM)')}</SelectItem>
                    <SelectItem value="afternoon">{t('booking.timeAfternoon', 'Afternoon (12PM - 5PM)')}</SelectItem>
                    <SelectItem value="evening">{t('booking.timeEvening', 'Evening (5PM - 8PM)')}</SelectItem>
                    <SelectItem value="golden-hour">{t('booking.timeGolden', 'Golden Hour (1 hour before sunset)')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {t('booking.location', 'Session Location *')}
                </span>
              </Label>
              <Input
                id="location"
                name="location"
                placeholder={t('booking.locationPlaceholder', 'Studio, outdoor location, your venue, etc.')}
                required
              />
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('booking.notes', 'Additional Notes')}</Label>
            <Textarea
              id="notes"
              name="notes"
              placeholder={t('booking.notesPlaceholder', 'Tell us about your vision, special requests, number of people, etc.')}
              rows={3}
            />
          </div>

          {/* Price & Promo Code Section */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-4 border">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-muted-foreground">{t('booking.total', 'Total Price')}</span>
              <div className="flex flex-col items-end">
                {appliedDiscount ? (
                  <>
                    <span className="text-sm line-through text-muted-foreground">
                      {formatPrice(service.base_price)}
                    </span>
                    <div className="flex items-center gap-2 text-green-600 font-bold text-xl">
                      <Tag className="h-4 w-4" />
                      {formatPrice(finalPrice)}
                    </div>
                  </>
                ) : (
                  <span className="font-bold text-xl text-primary">
                    {formatPrice(service.base_price)}
                  </span>
                )}
              </div>
            </div>

            {/* Integrated Promo/Referral Code Input */}
            <div>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                <Input
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    if (promoMessage) setPromoMessage(null);
                  }}
                  placeholder={t('booking.codePlaceholder', 'Have a promo or referral code?')}
                  className="pl-9 pr-24 h-11"
                  disabled={!!appliedDiscount}
                />
                <div className="absolute right-1 top-1 bottom-1 flex items-center">
                  {appliedDiscount ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 px-3 text-red-500 hover:text-red-600 hover:bg-red-50 font-medium"
                      onClick={() => {
                        setAppliedDiscount(null);
                        setPromoCode("");
                        setPromoMessage(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-1" />
                      {t('common.remove', 'Remove')}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant={promoCode ? "default" : "secondary"}
                      size="sm"
                      className="h-9 px-4 font-medium transition-all"
                      onClick={handleApplyPromoCode}
                      disabled={isValidatingCode || !promoCode}
                    >
                      {isValidatingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : t('booking.apply', 'Apply')}
                    </Button>
                  )}
                </div>
              </div>

              {/* Display salesperson name for referral codes */}
              {appliedDiscount?.code_type === 'REFERRAL' && appliedDiscount.salesperson_name && (
                <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                  <User className="h-4 w-4" />
                  <span>Referred by: <strong>{appliedDiscount.salesperson_name}</strong></span>
                </div>
              )}
              {promoMessage && (
                <div className={`text-sm mt-2 font-medium flex items-center gap-1 ${promoMessage.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                  {promoMessage.type === 'success' ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                  {promoMessage.text}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? t('booking.sending', 'Sending Request...') : t('booking.submit', 'Send Booking Request')}
            </Button>
          </div>
        </form>

        <div className="text-xs text-muted-foreground text-center pt-2 border-t">
          * {t('booking.disclaimer', 'This is a booking request. Final confirmation and payment details will be discussed via email or phone.')}
        </div>
      </DialogContent>
    </Dialog>
  );
}